import React, { useState, useEffect, useRef, useCallback } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import {
  buildOktaWidgetConfiguration,
  oktaWidgetID,
  IDP_DISCOVERY,
  PRIMARY_AUTH,
  getOauthBrowsingContextOrigin,
  getAppsFromAccessToken,
  applyRedirectFromOriginalUri,
} from '../../okta';
import { applyResolvedOidcOptionsToOktaAuth, oktaAuthJSClient, OAUTH_POPUP_WINDOW_NAME } from '../../okta/okta-config';
import { setEhrOidcAuthMode } from '../../okta/okta-auth-mode';
import MonitorErrorImage from '../../assets/MonitorErrorImage';
import cn from 'classnames';
import qs from 'query-string';
import { useHistory } from 'react-router-dom';
import logger from '../../utils/logger';
import { useIsMounted } from '../../utils/hooks';
import {
  triggerIDPDiscovery,
  isEmbeddedInFrame,
  getStoredEhrProviderEmail,
  getStoredEhrOktaIdpId,
} from '../../utils/ehr-launch-utils';
import { ROUTES } from '../../navigation/routes';
import Loader from '@tcl-boron-prefabs/loader';

import './okta-theme.scss';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';
import '../../shared/styles.scss';

const PRIVACY_URL = 'https://www.tempus.com/privacypolicy/';
const TERMS_URL = 'https://www.tempus.com/providersponsor-termsofuse/';

/** EHR iframe flow: decide whether to bootstrap OAuth in the top window before mounting the widget. */
const EhrIframeOauthMode = Object.freeze({
  PENDING: 'pending',
  REDIRECTING: 'redirecting',
  WIDGET: 'widget',
});

let oktaWidgetSingleton = null;

/**
 * Load @okta/okta-signin-widget only when the login UI is needed. A static import runs the
 * widget bundle on every SignIn load and can trigger Okta iframe requests (CSP) before an
 * EHR iframe top-window OAuth redirect runs.
 */
function ensureOktaWidgetInstance() {
  if (!oktaWidgetSingleton) {
    oktaWidgetSingleton = initializeOktaWidget();
  }
  return oktaWidgetSingleton;
}

/** Lazy singleton: OktaSignIn is created on first access (WIDGET mode), not when this module loads. */
export const oktaWidgetInstance = new Proxy(
  {},
  {
    get(_target, prop) {
      const inst = ensureOktaWidgetInstance();
      const value = inst[prop];
      return typeof value === 'function' ? value.bind(inst) : value;
    },
    set(_target, prop, value) {
      ensureOktaWidgetInstance()[prop] = value;
      return true;
    },
  },
);

/**
 * After top-window Okta login, restore to Account UI with fromLogin=true so SignIn runs SSO token handoff
 * (Okta sees existing session). Preserves other query params (e.g. fromEHR, redirect).
 */
function buildEhrPostSsoOriginalUri() {
  const path = window.location.pathname || '/';
  const params = new URLSearchParams(window.location.search ? window.location.search.replace(/^\?/, '') : '');
  params.set('fromLogin', 'true');
  const query = params.toString();
  return `${getOauthBrowsingContextOrigin()}${path}${query ? `?${query}` : ''}`;
}

/**
 * URL for the first-party top-level popup that runs the EHR OAuth redirect. The PKCE transaction
 * must be created AND consumed in the same window, so the popup (not the iframe) starts the flow.
 */
function buildEhrSsoPopupUrl({ idp, loginHint, originalUri }) {
  const params = new URLSearchParams();
  params.set('ehrSsoStart', 'true');
  if (loginHint) {
    params.set('loginHint', loginHint);
  }
  if (idp) {
    params.set('idp', idp);
  }
  params.set('originalUri', originalUri);
  return `${getOauthBrowsingContextOrigin()}/?${params.toString()}`;
}

/**
 * Start the Okta OAuth (PKCE) redirect. Must run where /callback/ehr is later handled so okta-auth-js
 * writes and reads the PKCE codeVerifier in the same storage context.
 *
 * `navigateInFrame` forces the redirect to happen in the CURRENT window. In an embedded EHR iframe the
 * default `setLocation` (assignOauthRedirectLocation) prefers top-window then popup; for silent
 * institution SSO (idp) we instead want the iframe to navigate itself so the whole flow stays embedded.
 */
function startEhrOauthRedirect({ idp, loginHint, originalUri, navigateInFrame = false }) {
  setEhrOidcAuthMode();
  applyResolvedOidcOptionsToOktaAuth(oktaAuthJSClient);
  try {
    oktaAuthJSClient.transactionManager?.clear?.();
  } catch {
    // noop
  }
  const tokenParams = { originalUri: originalUri || buildEhrPostSsoOriginalUri() };
  if (loginHint) {
    tokenParams.loginHint = loginHint;
  }
  if (idp) {
    tokenParams.idp = idp;
  }
  if (navigateInFrame) {
    const previousSetLocation = oktaAuthJSClient.options.setLocation;
    oktaAuthJSClient.options.setLocation = (href) => window.location.assign(href);
    return oktaAuthJSClient.token.getWithRedirect(tokenParams).catch((err) => {
      // Restore only on failure; on success the window navigates away before this runs.
      oktaAuthJSClient.options.setLocation = previousSetLocation;
      throw err;
    });
  }
  return oktaAuthJSClient.token.getWithRedirect(tokenParams);
}

/** ms to wait for silent (prompt=none) EHR auth before falling back to interactive login. */
const EHR_SILENT_AUTH_TIMEOUT_MS = 5000;

/**
 * Attempt EHR OAuth silently (prompt=none) in a hidden iframe. Resolves with tokens when the user
 * already has a session at Okta / the routed IdP / Auth0 — the common EHR-launch case — so the
 * launch completes fully embedded with no popup and no navigation. Throws otherwise (e.g.
 * login_required, or when third-party cookies to the Okta domain are blocked), which drives the
 * interactive fallback.
 */
async function getEhrTokensWithoutPrompt({ idp, loginHint }) {
  setEhrOidcAuthMode();
  applyResolvedOidcOptionsToOktaAuth(oktaAuthJSClient);
  const params = { timeout: EHR_SILENT_AUTH_TIMEOUT_MS };
  if (idp) {
    params.idp = idp;
  }
  if (loginHint) {
    params.loginHint = loginHint;
  }
  const { tokens } = await oktaAuthJSClient.token.getWithoutPrompt(params);
  await oktaAuthJSClient.tokenManager.setTokens(tokens);
  await oktaAuthJSClient.authStateManager.updateAuthState();
  return tokens;
}

/** After silent auth applies tokens, navigate the embedded iframe to the app switcher. */
function navigateToAppSwitcher({ tokens, originalUri, history }) {
  applyRedirectFromOriginalUri(originalUri);
  const authState = oktaAuthJSClient.authStateManager.getAuthState();
  const accessToken = authState?.accessToken ?? tokens.accessToken;
  const apps = getAppsFromAccessToken(accessToken);
  history.push(ROUTES.APP_SWITCHER, { apps });
}

export default function OktaWidgetWrapper() {
  const history = useHistory();
  const widgetRef = useRef(null);
  const isMounted = useIsMounted();

  const [error, setError] = useState(undefined);
  const [ehrIframeOAuthMode, setEhrIframeOAuthMode] = useState(EhrIframeOauthMode.PENDING);
  const [widgetRenderKey, setWidgetRenderKey] = useState(0);

  const renderTroubleLoggingIn = useCallback(() => {
    const TEXT = 'Trouble logging in?';
    const submitLink = document.querySelector('#okta-signin-submit');

    // Render 'Trouble logging in?' button below 'Log in'
    if (submitLink) {
      const div = document.createElement('div');

      div.innerHTML = `
      <div class="custom-button trouble-logging-in-button" data-testid="trouble-logging-in-button" id="trouble-logging-in-button"
      >
        ${TEXT}
      </div>`;
      div.onclick = () => {
        history.push(ROUTES.TROUBLE_LOGGING_IN);
      };
      submitLink.parentElement.appendChild(div);
    }
  }, [history]);

  const renderDisclaimer = useCallback((_, isActivationPage) => {
    const DEFAULT_MESSAGE = 'By logging in';
    const NEW_ACCOUNT_MESSAGE = 'By creating an account';
    const submitLink = document.querySelector('#okta-signin-submit');

    // Render disclaimer below 'Trouble logging in?' button
    if (submitLink) {
      const div = document.createElement('div');
      const actionText = isActivationPage ? NEW_ACCOUNT_MESSAGE : DEFAULT_MESSAGE;

      div.innerHTML = `
      <div class="terms-disclaimer-container" data-testid="terms-disclaimer-container" style="text-align:center" id="login-disclaimer">
        ${actionText}, you agree to our <a class="legal-disclaimer-link" target="_blank" href="${TERMS_URL}" style="color:#206ef3">Terms of Use</a> and
        <a class="legal-disclaimer-link" target="_blank" href="${PRIVACY_URL}" style="color:#206ef3">Privacy Policy</a>
      </div>`; //ActionPalette300=#206ef3
      submitLink.parentElement.appendChild(div);
    }
  }, []);

  const clearAllCustomComponents = useCallback(() => {
    const renderedDomElements = {
      troubleLoggingIn: document.getElementById('trouble-logging-in-button'),
      disclaimer: document.getElementById('login-disclaimer'),
    };

    Object.keys(renderedDomElements).forEach((key) => {
      if (renderedDomElements[key]) {
        renderedDomElements[key]?.remove();
      }
    });
  }, []);

  const renderAllCustomComponents = useCallback(
    (context, isActivationPage) => {
      renderDisclaimer(context, isActivationPage);
    },
    [renderDisclaimer],
  );

  useEffect(() => {
    const queryObj = qs.parse(window.location.search);

    /**
     * First-party top-level popup opened from the EHR iframe. Create AND consume the PKCE
     * transaction here: /callback/ehr also runs in this window, so okta-auth-js can read the
     * codeVerifier it wrote. (Starting the flow in the iframe leaves the verifier in the iframe's
     * sessionStorage, which the popup can't read → "Could not load PKCE codeVerifier from storage".)
     */
    if (queryObj.ehrSsoStart) {
      setEhrIframeOAuthMode(EhrIframeOauthMode.REDIRECTING);
      startEhrOauthRedirect({
        idp: queryObj.idp || undefined,
        loginHint: queryObj.loginHint || undefined,
        originalUri: queryObj.originalUri || undefined,
      }).catch((err) => {
        logger.error('EHR SSO popup OAuth redirect failed; loading sign-in widget', { err });
        setEhrIframeOAuthMode(EhrIframeOauthMode.WIDGET);
      });
      return;
    }

    if (queryObj.fromEHR) {
      setEhrOidcAuthMode();
      applyResolvedOidcOptionsToOktaAuth(oktaAuthJSClient);
    }
    if (!isEmbeddedInFrame()) {
      setEhrIframeOAuthMode(EhrIframeOauthMode.WIDGET);
      return;
    }
    if (queryObj.fromLogin || oktaAuthJSClient.isLoginRedirect()) {
      setEhrIframeOAuthMode(EhrIframeOauthMode.WIDGET);
      return;
    }

    /**
     * EHR iframe launch — silent-first cascade:
     *
     * 1. SILENT (prompt=none): try to complete OAuth with no UI in a hidden iframe. Works whenever the
     *    user already has a session (Okta, the routed EHR IdP, or Auth0) — the common EHR-launch case.
     *    Stays fully embedded: no popup, no navigation. This is IdP-independent.
     * 2. On silent failure (login_required / interaction needed / blocked third-party cookies), fall
     *    back to interactive login:
     *    - `idp` (institution SSO): redirect IN the embedded iframe. Popping out to the EHR's own OAuth
     *      (e.g. Epic vendorservices) in a Hyperdrive popup crashes Epic, so never use a popup here.
     *    - otherwise (Auth0 / external username-password): open a first-party top-level popup — the
     *      login form cannot render inside an iframe (frame-ancestors).
     */
    if (queryObj.fromEHR) {
      const email = getStoredEhrProviderEmail();
      const oktaIdpId = getStoredEhrOktaIdpId();
      const originalUri = buildEhrPostSsoOriginalUri();
      setEhrIframeOAuthMode(EhrIframeOauthMode.REDIRECTING);

      const fallbackToWidget = () => setEhrIframeOAuthMode(EhrIframeOauthMode.WIDGET);

      const startInteractiveLogin = () => {
        // Institution SSO: silent, frameable — redirect in place (no popup → no Hyperdrive crash).
        if (oktaIdpId) {
          startEhrOauthRedirect({
            idp: oktaIdpId,
            loginHint: email || undefined,
            originalUri,
            navigateInFrame: true,
          }).catch((err) => {
            logger.error('EHR in-frame idp OAuth redirect failed; loading sign-in widget', { err });
            fallbackToWidget();
          });
          return;
        }
        // Auth0 / external: interactive login UI can't render in an iframe → top-level popup.
        const popup = window.open(buildEhrSsoPopupUrl({ loginHint: email, originalUri }), OAUTH_POPUP_WINDOW_NAME);
        if (!popup) {
          logger.warn('EHR SSO popup blocked; falling back to in-frame OAuth redirect');
          startEhrOauthRedirect({
            loginHint: email || undefined,
            originalUri,
            navigateInFrame: true,
          }).catch((err) => {
            logger.error('EHR iframe OAuth redirect failed; loading sign-in widget', { err });
            fallbackToWidget();
          });
        }
      };

      getEhrTokensWithoutPrompt({ idp: oktaIdpId, loginHint: email })
        .then((tokens) => {
          logger.info('EHR Smart on FHIR: completed OAuth silently in the embedded iframe', { idp: oktaIdpId });
          navigateToAppSwitcher({ tokens, originalUri, history });
        })
        .catch((silentErr) => {
          logger.info('EHR silent auth unavailable; falling back to interactive login', {
            reason: silentErr?.errorCode || silentErr?.message,
            idp: oktaIdpId,
          });
          startInteractiveLogin();
        });
      return;
    }
    setEhrIframeOAuthMode(EhrIframeOauthMode.WIDGET);
  }, [history]);

  useEffect(() => {
    if (ehrIframeOAuthMode !== EhrIframeOauthMode.WIDGET) {
      return;
    }
    if (!widgetRef.current) {
      return;
    }

    const queryObj = qs.parse(window.location.search);
    if (queryObj.idp && !queryObj.fromEHR && !queryObj.fromLogin) {
      const returnParams = new URLSearchParams(window.location.search);
      returnParams.delete('idp');
      returnParams.set('fromLogin', 'true');
      const originalUri = `${getOauthBrowsingContextOrigin()}${window.location.pathname || '/'}?${returnParams}`;
      oktaAuthJSClient.token.getWithRedirect({ idp: queryObj.idp, originalUri }).catch((err) => {
        logger.error('IdP hint OAuth redirect failed; loading sign-in widget', { err, idp: queryObj.idp });
        returnParams.delete('fromLogin');
        const cleanUrl = `${window.location.pathname || '/'}?${returnParams}`;
        window.history.replaceState({}, '', cleanUrl);
        setWidgetRenderKey((k) => k + 1);
      });
      return;
    }

    oktaWidgetInstance.showSignInAndRedirect({ el: oktaWidgetID });

    clearAllCustomComponents();

    oktaWidgetInstance.on('afterRender', (context) => {
      const { activationToken } = qs.parse(window.location.search);
      const isActivationPage = !!activationToken;

      if (isMounted()) {
        const contextMap = {
          // Page with Email and Password fields
          [PRIMARY_AUTH]: () => renderAllCustomComponents(context, isActivationPage),
          // Page with just Email field
          [IDP_DISCOVERY]: () => renderTroubleLoggingIn(),
        };

        // Handle EHR launch IDP Discovery, needs to be done after the widget is rendered
        const queryObj = qs.parse(window.location.search);
        const hasFromEHRQueryParam = queryObj?.fromEHR;
        const hasFromLoginQueryParam = queryObj?.fromLogin;

        // Only trigger IDP discovery if we have fromEHR but NOT fromLogin
        // fromLogin means we're returning from SSO, so we should complete auth instead
        if (hasFromEHRQueryParam && !hasFromLoginQueryParam) {
          triggerIDPDiscovery();
        }

        return contextMap[context.controller]() ?? null;
      }
    });

    oktaWidgetInstance.on('afterError', (context, error) => {
      if (!isMounted()) {
        logger.error('Error loading signin widget', { error, context });
        setError('Error loading signin widget');
      }
    });

    return () => {
      removeWidget();
    };
  }, [
    ehrIframeOAuthMode,
    widgetRef,
    widgetRenderKey,
    clearAllCustomComponents,
    isMounted,
    renderAllCustomComponents,
    renderTroubleLoggingIn,
  ]);

  if (error)
    return (
      <div className="app-view-container" data-test-id="login-error-container" style={{ textAlign: 'center' }}>
        <MonitorErrorImage />
        <h4 className={cn(typography.H4)}>SOMETHING WENT WRONG</h4>
        <p>
          There was an issue loading this page.
          <br />
          Try refreshing the page or contact us to resolve the issue.
        </p>
        <p>
          <a className={cn(typography.link)} href="mailto:support@tempus.com">
            Contact Tempus support
          </a>
        </p>
      </div>
    );

  if (ehrIframeOAuthMode !== EhrIframeOauthMode.WIDGET) {
    return (
      <div className={cn('app-view-container')} data-testid="ehr-iframe-oauth-loading">
        <Loader />
      </div>
    );
  }

  return (
    <React.Fragment>
      <div id={oktaWidgetID.replace('#', '')} data-testid={oktaWidgetID.replace('#', '')} ref={widgetRef} />
    </React.Fragment>
  );
}

export function isLoginRedirect() {
  return oktaAuthJSClient.isLoginRedirect();
}

export async function getWithRedirect(params) {
  await oktaAuthJSClient.token.getWithRedirect(params);
}

export function removeWidget() {
  oktaWidgetSingleton?.remove();
}

export function hideWidget() {
  oktaWidgetSingleton?.hide();
}

export async function showSignInAndRedirect() {
  await oktaWidgetInstance.showSignInAndRedirect();
}

export function initializeOktaWidget(OktaSignInClass) {
  // Allow authorized clients to opt in to oauth implicit flow.
  const { recoveryToken, activationToken } = qs.parse(window.location.search);

  const oktaSettings = cloneDeep(buildOktaWidgetConfiguration());

  if (recoveryToken) {
    oktaSettings.recoveryToken = recoveryToken;
  }

  if (activationToken) {
    oktaSettings.recoveryToken = activationToken;
    oktaSettings.i18n.en['password.reset.title.generic'] = 'Set your password';
    oktaSettings.i18n.en['password.reset'] = 'Set Password';
  }

  const { fromEHR } = qs.parse(window.location.search);
  const ehrOktaIdpId = getStoredEhrOktaIdpId();
  if (fromEHR && ehrOktaIdpId) {
    oktaSettings.authParams = {
      ...oktaSettings.authParams,
      idp: ehrOktaIdpId,
    };
  }

  // Same help links for all app deep-links; EHR iframe flow uses top-window OAuth elsewhere (EhrIframeOauthMode).
  oktaSettings.helpLinks = {
    custom: [
      {
        text: 'Request Access',
        href: 'https://go.tempus.com/access-request',
      },
      {
        text: 'Trouble logging in?',
        href: `${window.location.origin}/trouble-logging-in`,
      },
    ],
  };

  const SignIn = resolveOktaSignInConstructor(OktaSignInClass);
  return new SignIn(oktaSettings);
}

/** Webpack `require` may expose the widget as `default` or as the module itself. */
function resolveOktaSignInConstructor(OktaSignInClass) {
  if (OktaSignInClass) {
    return OktaSignInClass;
  }
  // eslint-disable-next-line global-require, import/no-dynamic-require
  const mod = require('@okta/okta-signin-widget');
  return mod?.default ?? mod;
}
